/**
 * Область поиска (C-28, изменение v1.15).
 *
 * Лупа рядом с панелью вкладок ищет по той вкладке, что открыта сейчас, —
 * иначе из «Настроек» она открывала людей и чаты, и интерфейс «путался»:
 *
 *   «Контакты» → люди: контакты по имени и никнейму, глобальный поиск
 *   «Движ»     → люди — тот же поиск: во вкладке живут люди и подбор
 *   «Чаты»     → чаты и сообщения (вкладки «Чаты» · «Сообщения»)
 *   «Настройки» → сами настройки (указатель `settings-index.ts`)
 *
 * Без области (старый deep link `/search`) экран показывает все виды сразу.
 */

export type SearchScope = 'contacts' | 'dvizh' | 'chats' | 'settings'

/** Вид результатов внутри экрана поиска. */
export type SearchCategory = 'people' | 'chats' | 'messages' | 'settings'

const SCOPES: readonly SearchScope[] = ['contacts', 'dvizh', 'chats', 'settings']

export function isSearchScope(value: unknown): value is SearchScope {
  return typeof value === 'string' && (SCOPES as readonly string[]).includes(value)
}

/** Вкладка панели (`/contacts`, `/chats` …) → область поиска; иначе без области. */
export function scopeForSection(section: string): SearchScope | undefined {
  const scope = section.replace(/^\//, '')
  return isSearchScope(scope) ? scope : undefined
}

/** Какие виды результатов есть в области — первый открывается сразу. */
export function categoriesFor(scope: SearchScope | undefined): readonly SearchCategory[] {
  switch (scope) {
    case 'contacts':
    case 'dvizh':
      return ['people']
    case 'chats':
      return ['chats', 'messages']
    case 'settings':
      return ['settings']
    default:
      return ['people', 'chats', 'messages']
  }
}

/** Подсказка в поле — говорит, что именно ищем здесь. */
export function placeholderFor(scope: SearchScope | undefined, category: SearchCategory): string {
  if (category === 'settings') return 'Поиск по настройкам'
  if (category === 'messages') return 'Текст сообщения'
  if (scope === 'chats') return 'Название чата или канала'
  if (scope === 'contacts' || scope === 'dvizh') return 'Имя или @никнейм'
  return 'Имя, название или @никнейм'
}

/** Имя поля для скринридера — по области. */
export function fieldLabelFor(scope: SearchScope | undefined): string {
  switch (scope) {
    case 'contacts':
    case 'dvizh':
      return 'Поиск людей'
    case 'chats':
      return 'Поиск по чатам и сообщениям'
    case 'settings':
      return 'Поиск по настройкам'
    default:
      return 'Поиск людей, чатов и сообщений'
  }
}
